import { useRef } from 'react';
import { useClickAway } from 'react-use';
import Spinner, { SpinnerSize } from '../loading/spinner';

interface DropdownMenuProps {
  isDropdownOpen: boolean;
  children: JSX.Element | JSX.Element[];
  buttonIconClasses: string;
  buttonClasses: string;
  buttonLabel?: string;
  buttonLabelClasses?: string;
  hasExtraButtonIconClasses?: boolean;
  extraButtonIconClasses?: string;
  dropdownMenuClasses?: string;
  containerClasses?: string;
  isDisabled?: boolean;
  title?: string;
  isLoading?: boolean;
  onDropdownClicked: () => void;
  onClickAway: () => void;
}

const DropdownMenu = ({
  isDropdownOpen,
  children,
  buttonIconClasses,
  buttonClasses,
  buttonLabel,
  buttonLabelClasses,
  hasExtraButtonIconClasses,
  extraButtonIconClasses,
  dropdownMenuClasses,
  containerClasses,
  isDisabled = false,
  title,
  isLoading,
  onDropdownClicked,
  onClickAway
}: DropdownMenuProps) => {
  const ref = useRef(null);

  useClickAway(ref, onClickAway);

  const disabledIconClasses = isDisabled ? 'text-neutral-200' : '';

  const buttonLabelClassesToUse = buttonLabelClasses ?? 'ml-2 text-sm';

  return (
    <>
      <div className={containerClasses ?? 'block relative'} ref={ref}>
        <button
          aria-haspopup="true"
          aria-controls="dropdown-menu"
          onClick={onDropdownClicked}
          className={`${buttonClasses}`}
          disabled={isDisabled}
          type="button"
          title={title}
        >
          {isLoading ? (
            <Spinner size={SpinnerSize.SMALL} />
          ) : (
            <>
              <i className={`${buttonIconClasses} ${disabledIconClasses}`}></i>
              {hasExtraButtonIconClasses ? (
                <>
                  <i
                    className={`${extraButtonIconClasses} ${disabledIconClasses}`}
                  ></i>
                </>
              ) : (
                <></>
              )}
            </>
          )}
          {buttonLabel ? (
            <>
              <span className={buttonLabelClassesToUse}>{buttonLabel}</span>
            </>
          ) : (
            <></>
          )}
        </button>
        {isDropdownOpen ? (
          <div
            className={`absolute drop-shadow-xl rounded ${
              dropdownMenuClasses ?? 'right-0 z-40'
            }`}
            role="menu"
          >
            <div className="bg-white flex flex-col border rounded border-neutral-200">
              {children}
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
};

export default DropdownMenu;
